import * as Sentry from '@sentry/react'
import type { DocumentImmediateResult } from '@numeo/types-core'
import { DocumentReadError } from '../../services/documentAutofillApi'
import { trackBoxTruckProfileDocumentAutofillRead, type BoxTruckAutofillReadOutcome, type BoxTruckDocumentScope } from '../analytics'
import { interpretRead, type AutofillProblem, type AutofillTarget, type AutofillValue } from './targets'

/**
 * How one file is read: a file chosen in the form is uploaded with the request, a saved document is read by the server from storage.
 * Either way the browser holds nothing to keep for Try again: a chosen file is already in the form, a saved one never comes down.
 */
export type ReadDocument = (docTypeHint: string, requestKey: string, signal: AbortSignal) => Promise<DocumentImmediateResult>

/** One file's read, kept so the card is planned again when another file is added or removed, and so it can be tried again. */
export interface FileRead<K extends string> {
    fileName: string
    read: ReadDocument
    /**
     * The key Try again sends. The same as this read's while its job may still answer (a lost response, a timeout: the server answers
     * from the job it has); a new one once the job ended failed, since a resend under its key gets that same failure back.
     */
    requestKey: string
    retries: number
    outcome: { kind: 'facts'; values: Partial<Record<K, AutofillValue>> } | { kind: AutofillProblem; readAs?: string; cached?: boolean }
}

/** A file to read: new, or a failed read tried again (`prior`). */
export interface ReadJob<K extends string> {
    id: string
    fileName: string
    read: ReadDocument
    prior?: FileRead<K>
}

/**
 * Read one file of a card with the pipeline and say what it gave the card. Every outcome is reported (`_read`), and a failure no
 * known outcome explains goes to Sentry with its original error. Undefined when `signal` aborted (Cancel, the file removed, the form
 * closed): such a read says nothing and writes nothing.
 */
export async function readDocumentFile<K extends string>(
    scope: BoxTruckDocumentScope,
    card: string,
    target: AutofillTarget<K>,
    job: ReadJob<K>,
    signal: AbortSignal
): Promise<FileRead<K> | undefined> {
    const started = Date.now()
    const retries = job.prior ? job.prior.retries + 1 : 0
    const base = { fileName: job.fileName, read: job.read, requestKey: job.prior?.requestKey ?? crypto.randomUUID(), retries }
    const report = (outcome: BoxTruckAutofillReadOutcome, extra: { readAs?: string; cached?: boolean; facts?: Record<string, unknown> } = {}) =>
        trackBoxTruckProfileDocumentAutofillRead(scope, card, { outcome, attempt: retries, durationMs: Date.now() - started, ...extra })
    const ended = (outcome: FileRead<K>['outcome']): FileRead<K> => ({ ...base, outcome })

    if (signal.aborted) return undefined
    try {
        const result = await job.read(target.docTypeHint, base.requestKey, signal)
        if (signal.aborted) return undefined
        const outcome = interpretRead(result, target.docTypeHint)
        report(outcome.kind, { readAs: result.docType, cached: result.cached, facts: outcome.kind === 'facts' ? outcome.facts : undefined })
        if (outcome.kind === 'facts') return ended({ kind: 'facts', values: target.toFields(outcome.facts) })
        if (outcome.kind === 'wrong_document') return ended({ kind: outcome.kind, readAs: outcome.readAs, cached: outcome.cached })
        // The job itself ended failed: Try again starts a new one.
        if (outcome.kind === 'failed') return { ...ended({ kind: outcome.kind }), requestKey: crypto.randomUUID() }
        return ended({ kind: outcome.kind })
    } catch (error) {
        // An aborted read says nothing: a newer read or Cancel owns the card now.
        if (signal.aborted) return undefined
        const kind = error instanceof DocumentReadError ? error.kind : 'failed'
        if (kind === 'cancelled') return undefined
        // A failure no known outcome explains is reported with its original error; an expected one (busy, refused, slow) is not.
        const unexpected = error instanceof DocumentReadError ? error.unexpected : error
        if (unexpected !== undefined) Sentry.captureException(unexpected, { tags: { feature: 'profile-document-autofill', operation: 'read' } })
        report(kind)
        return ended({ kind })
    }
}
